import {
    DndContext,
    DragEndEvent,
    DraggableSyntheticListeners,
    useDraggable,
} from '@dnd-kit/core';
import { Dialog, DialogProps, Stack, SxProps, Theme } from '@mui/material';
import { GripVertical } from 'lucide-react';
import {
    createContext,
    ReactNode,
    useCallback,
    useContext,
    useState,
} from 'react';

const DialogDragHandleContext =
    createContext<DraggableSyntheticListeners>(undefined);

/**
 * A non-blocking, draggable MUI Dialog: it renders no backdrop, the page
 * around the paper stays fully interactive (pointer events pass through
 * the fullscreen modal container), and the paper is dragged by a
 * {@link DialogDragHandle} placed anywhere in the children (typically in
 * the title bar). This is the interaction model of the operation panels
 * ({@link UnifiedOperationPanel}) in Dialog form — for dialogs whose
 * content refers to the viewports behind them, e.g. one that holds an
 * on-viewport preview a modal backdrop would obscure.
 *
 * Focus is deliberately not trapped and the body scroll lock is off, so
 * the user can scroll slices and pick tools in the viewports while the
 * dialog is open. Consequences the caller should expect: there is no
 * backdrop-click close (the paper only closes through its own controls
 * or Escape while focus is within), and Escape reaches the dialog only
 * while focus is inside it. The drag offset lives in this component's
 * state, so a dialog that unmounts on close reopens centred.
 */
export default function DraggableDialog(props: DialogProps) {
    const [offset, setOffset] = useState({ x: 0, y: 0 });

    const handleDragEnd = useCallback((event: DragEndEvent) => {
        setOffset((prev) => ({
            x: prev.x + event.delta.x,
            y: prev.y + event.delta.y,
        }));
    }, []);

    // The DndContext must be an ANCESTOR of the component calling
    // useDraggable, so the dialog body is a separate component below it.
    return (
        <DndContext onDragEnd={handleDragEnd}>
            <DraggableDialogBody offset={offset} {...props} />
        </DndContext>
    );
}

interface DraggableDialogBodyProps extends DialogProps {
    offset: { x: number; y: number };
}

function DraggableDialogBody({
    offset,
    PaperProps,
    sx,
    children,
    ...dialogProps
}: DraggableDialogBodyProps) {
    const { listeners, setNodeRef, transform, isDragging } = useDraggable({
        id: 'draggable-dialog',
    });
    // Committed offset plus the in-flight drag delta, so the paper follows
    // the pointer live and does not jump when the drag ends.
    const x = offset.x + (transform?.x ?? 0);
    const y = offset.y + (transform?.y ?? 0);

    return (
        <Dialog
            {...dialogProps}
            hideBackdrop
            disableEnforceFocus
            disableScrollLock
            sx={{ pointerEvents: 'none', ...sx }}
            PaperProps={{
                ...PaperProps,
                // dnd-kit's aria attributes are NOT spread here: they would
                // stamp role="button" over MUI's role="dialog". Keyboard
                // repositioning is forgone — the dialog is fully usable
                // where it opens; dragging is a pointer refinement.
                ref: setNodeRef,
                sx: {
                    pointerEvents: 'auto',
                    // Relative to the flex-centred resting position, so an
                    // untouched dialog sits exactly where a modal one would.
                    transform: `translate(${x}px, ${y}px)`,
                    cursor: isDragging ? 'grabbing' : undefined,
                    ...PaperProps?.sx,
                },
            }}
        >
            <DialogDragHandleContext.Provider value={listeners}>
                {children}
            </DialogDragHandleContext.Provider>
        </Dialog>
    );
}

interface DialogDragHandleProps {
    children: ReactNode;
    sx?: SxProps<Theme>;
}

/**
 * The element that drags the enclosing {@link DraggableDialog} — same
 * grip affordance as the operation panels' {@link PanelHeader}. Keep
 * interactive controls (the close button) OUTSIDE the handle: a pointer
 * press on the handle arms the drag sensor.
 */
export function DialogDragHandle({ children, sx }: DialogDragHandleProps) {
    const listeners = useContext(DialogDragHandleContext);
    return (
        <Stack
            direction="row"
            alignItems="center"
            gap={0.5}
            {...listeners}
            sx={{
                cursor: 'grab',
                '&:active': { cursor: 'grabbing' },
                userSelect: 'none',
                touchAction: 'none',
                ...sx,
            }}
        >
            <GripVertical size={12} style={{ opacity: 0.6, flexShrink: 0 }} />
            {children}
        </Stack>
    );
}
